import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Play, X, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'all', label: 'All Videos', playlist: null },
  { id: 'news', label: 'The Who Cares News', playlist: 'https://youtube.com/playlist?list=PLn8UbQXnz_ExF9R4UwNlqHAHF-d7OM24V&si=PobVwdyM2w5GqSQT' },
  { id: 'music', label: 'GradTrax Music', playlist: 'https://www.youtube.com/playlist?list=PLn8UbQXnz_EwxLHHytTn17kdp69kZBNvn' },
  { id: 'fairytale', label: 'WTFairytale', playlist: 'https://youtube.com/playlist?list=PLn8UbQXnz_EwRgkLIlru3qUNH_7XQs8kf&si=oE1gwXjFsfAF0115' },
  { id: 'courses', label: 'Courses', playlist: 'https://www.youtube.com/playlist?list=PLn8UbQXnz_Ez1jG-k_kblXaK5R_CeQTg6' },
];

const categoryCards = [
  { 
    id: 'news', 
    title: 'The Who Cares News', 
    description: 'Apathetic Journalism for the Indifferent World',
    image: 'https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/u0e9963i_cvr1.png',
    link: 'https://youtube.com/playlist?list=PLn8UbQXnz_ExF9R4UwNlqHAHF-d7OM24V&si=PobVwdyM2w5GqSQT'
  },
  { 
    id: 'music', 
    title: 'GradTrax Music', 
    description: 'Original AI-assisted music and songs',
    image: 'https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/9261c41q_FRK5M.jpg',
    link: 'https://www.youtube.com/playlist?list=PLn8UbQXnz_EwxLHHytTn17kdp69kZBNvn'
  },
  { 
    id: 'fairytale', 
    title: 'WTFairytale', 
    description: 'Classic fairytales reimagined',
    image: 'https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/yezsf8gi_Copilot_20260419_202022.png',
    link: 'https://youtube.com/playlist?list=PLn8UbQXnz_EwRgkLIlru3qUNH_7XQs8kf&si=oE1gwXjFsfAF0115'
  },
  { 
    id: 'courses', 
    title: 'Courses', 
    description: 'Religion, Mythology, History, Science & Truth',
    image: 'https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/vuqns3de_Copilot_20260408_233150%20a.png',
    link: 'https://www.youtube.com/playlist?list=PLn8UbQXnz_Ez1jG-k_kblXaK5R_CeQTg6'
  }
];

const playlistLinks = [
  { name: "Soc 101", url: "https://www.youtube.com/playlist?list=PLn8UbQXnz_Ez1jG-k_kblXaK5R_CeQTg6" },
  { name: "Soc 102", url: "https://www.youtube.com/playlist?list=PLn8UbQXnz_Ewoos02QDqgk8p2PgsSkxwc" },
  { name: "Shorts", url: "https://youtube.com/playlist?list=PLn8UbQXnz_EzpTT3vjbPtSoesKbR6foau" },
  { name: "History of It All", url: "https://www.youtube.com/playlist?list=PLn8UbQXnz_EyD9w80jSJ-aQKTnHO0KKVt" },
  { name: "Spotify", url: "https://open.spotify.com/artist/2m8OD1Y5PSQmd5mBPQvR3r", isSpotify: true },
];

export default function VideosPage() {
  const [videos, setVideos] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const params = activeCategory !== 'all' ? `?category=${activeCategory}` : '';
      const response = await axios.get(`${API}/videos${params}`);
      setVideos(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  return (
    <div className="min-h-screen py-24 md:py-32" data-testid="videos-page">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="section-header mb-4">Video Library</h1>
          <p className="section-subheader max-w-2xl mx-auto">
            400+ handcrafted videos spanning satirical news, original music, fairytale adaptations, and social science content.
          </p>
        </motion.div>

        {/* Category Tabs */}
        <Tabs value={activeCategory} onValueChange={setActiveCategory} className="mb-8">
          <TabsList className="flex flex-wrap justify-center gap-2 bg-transparent h-auto p-0">
            {categories.map(category => (
              <TabsTrigger
                key={category.id}
                value={category.id}
                className={`category-pill ${
                  activeCategory === category.id ? 'category-pill-active' : 'category-pill-inactive'
                }`}
                data-testid={`category-${category.id}`}
              >
                {category.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Category Feature Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {categoryCards.map((card, index) => (
            <motion.a
              key={card.id}
              href={card.link}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="group"
            >
              <Card className="bg-card border-border/50 overflow-hidden card-hover h-full">
                <div className="aspect-square overflow-hidden">
                  <img 
                    src={card.image} 
                    alt={card.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <CardContent className="p-4">
                  <h3 className="font-bold text-sm mb-1">{card.title}</h3>
                  <p className="text-xs text-muted-foreground">{card.description}</p>
                </CardContent>
              </Card>
            </motion.a>
          ))}
        </div>

        {/* Playlist Quick Links */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          <a 
            href="https://youtube.com/@mgn1.6?si=BapxMq9KCFhLK9rk" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/20 hover:bg-secondary/30 rounded-full text-sm font-medium transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Main Channel
          </a>
          {playlistLinks.map(link => (
            <a 
              key={link.name}
              href={link.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 rounded-full text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              {link.name}
            </a>
          ))}
        </div>

        {/* Videos Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="aspect-video skeleton rounded-2xl" />
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {videos.map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card
                  className="bg-card border-border/50 overflow-hidden card-hover group cursor-pointer"
                  onClick={() => setSelectedVideo(video)}
                  data-testid={`video-card-${video.id}`}
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail_url}
                      alt={video.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1635340038191-96eea7fbd056?w=500';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center animate-pulse-glow">
                        <Play className="w-6 h-6 text-white ml-1" />
                      </div>
                    </div>
                    <span className="absolute top-3 left-3 badge-accent capitalize">{video.category}</span>
                  </div>
                  <CardContent className="p-5">
                    <h3 className="font-bold text-lg mb-2 line-clamp-1">{video.title}</h3>
                    <p className="text-muted-foreground text-sm line-clamp-2">{video.description}</p>
                    {video.published_at && (
                      <p className="text-xs text-muted-foreground mt-3">
                        {new Date(video.published_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}

        {videos.length === 0 && !loading && (
          <div className="text-center py-20">
            <p className="text-muted-foreground">No videos found in this category.</p>
          </div>
        )}
      </div>

      {/* Video Modal */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl p-0 bg-card border-border overflow-hidden">
          <DialogTitle className="sr-only">
            {selectedVideo?.title || 'Video Player'}
          </DialogTitle>
          {selectedVideo && (
            <>
              <div className="video-wrapper">
                <iframe
                  src={`https://www.youtube.com/embed/${selectedVideo.video_id}?autoplay=1`}
                  title={selectedVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="p-6">
                <span className="badge-accent capitalize mb-2 inline-block">{selectedVideo.category}</span>
                <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display' }}>
                  {selectedVideo.title}
                </h2>
                <p className="text-muted-foreground">{selectedVideo.description}</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
